/* MINA 6-Pack — PDP offer control. Adds ONE line of the 3-Pack variant at quantity 2 x the stepper.
 *
 * NO LINE PROPERTIES. UpCart wipes the cart when a subscription line carries any property
 * (snippets/sf-sub-bundle-cart.liquid:12-14), so the payload carries exactly id, quantity and
 * selling_plan. This is not a style preference.
 *
 * NO MONEY STRINGS. Liquid rendered a figure for every mode and every stepper value up front; this
 * file only toggles which one is visible. The card cannot drift from the page if the script cannot
 * write to it (assets/sf-sub-bundle-picker.js:9-10).
 *
 * THE NATIVE PICKER IS LIVE (D1). All variant truth arrives on the theme's own `variant:change`
 * event; nothing is cached at load. Precedent for the listener:
 * snippets/combined-products-mina-sale-native.liquid:527-540. The theme constructs and dispatches it
 * from the product form with bubbles:true at assets/theme.js:3833-3840.
 *
 * WE ARE NOT THE ONLY LISTENER. assets/market-block.js:96 listens for the same event and, at
 * :86-88, writes `el.hidden` over every [data-market-hideable] element in the product's
 * <product-rerender> -- which includes the Loop widget's wrapper (product-info.liquid:733). That
 * work is debounced (:92-94), so it lands AFTER anything we do synchronously. Suppression here
 * therefore uses an owned class and never `hidden`. See D3.
 */
(() => {
  const root = document.querySelector('[data-six-pack]');
  if (!root) return;

  /* Repo convention: never a hardcoded '/cart.js'. A localized market prefixes the path.
   * snippets/sf-sub-bundle-cart.liquid:237, used at assets/sf-sub-bundle-picker.js:409. */
  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const panels     = Array.from(root.querySelectorAll('[data-sp-offer]'));
  const actions    = root.querySelector('[data-sp-actions]');
  /* Rendered by the theme's own snippets/button.liquid, which takes a `class` but not arbitrary
   * attributes -- so this is found by our class rather than a data-* hook (spec §5.3.2, item 4). */
  const addBtn     = root.querySelector('.six-pack__add');
  const addWrap    = root.querySelector('[data-sp-add-wrap]');
  const msgEl      = root.querySelector('[data-sp-msg]');
  /* Round 2. The Single and 3-Pack cells' per-filtration-type price pairs, and the two tooltip
   * triggers. Captured once, like every handle here -- safe ONLY because this block never
   * rerenders (six_pack is absent from block_types_allowing_partial_rerender). */
  const cellFigs   = Array.from(root.querySelectorAll('[data-sp-cellfig]'));
  /* Round 3 (spec 5.3.4). The 6-Pack cells' one-time/subscribed figure pairs; the per-variant
   * figure sources for Single and 3-Pack (top price, struck price, "Save X%", Loop's struck price);
   * and the "extra X% with subscription" callout with its Liquid-rendered lines. All ours and all
   * inside this never-rerendered block, so capturing them once is safe. The callout is MOVED out of
   * this block while Single or 3-Pack is selected, but it is the same element, so its handle holds. */
  const sixFigs    = Array.from(root.querySelectorAll('[data-sp-sixfig]'));
  const vsrcs      = Array.from(root.querySelectorAll('[data-sp-vsrc]'));
  const callout    = root.querySelector('[data-sp-callout]');
  const calloutTexts = callout ? Array.from(callout.querySelectorAll('[data-sp-callout-for]')) : [];
  const tips       = Array.from(root.querySelectorAll('[data-sp-tip]'));
  const PACK_PARAM = root.dataset.packParam || '';
  const TYPE_PARAM = root.dataset.typeParam || '';
  const COPY = {
    collide:   root.dataset.msgCollide   || '',
    stock:     root.dataset.msgStock     || '',
    stockMany: root.dataset.msgStockMany || '',
    error:     root.dataset.msgError     || '',
    cartcheck: root.dataset.msgCartcheck || ''
  };

  /* No button means no way to add, so bail BEFORE suppressing anything: the native picker and the
   * theme's own buy controls then stay exactly as they are, and the page is still buyable. That is
   * the degraded case this design always takes -- inert markup, working page. */
  if (!panels.length || !addBtn) return;

  /* R3-12 (Sanjeet, 2026-09-22): THE ARRIVAL PURCHASE DEFAULT, read off the markup rather than
   * written here. snippets/six-pack-picker.liquid puts `checked` on one of the two purchase radios;
   * that attribute is the whole page's default -- our own 6-Pack fieldset takes it as state.mode,
   * and the boot intent at the foot of this file asks LOOP's widget for the same option on the
   * Single and the 3-Pack. One declaration, in the Liquid, for all three pack sizes.
   *
   * Read ONCE, here, and only here: render() rewrites `checked` over the active fieldset on its very
   * first pass, so anything read later would be reading our own state back. Every fieldset is
   * rendered with the same default, so the first one answers for all of them.
   *
   * Anything unreadable is 'onetime' -- the theme's own figures, the page as it was. */
  const DEFAULT_MODE =
    (root.querySelector('[data-sp-mode]:checked') || {}).value === 'subscribe' ? 'subscribe' : 'onetime';

  /* All mutable state lives here, not in the DOM. The panels are pure presentation, which is what
   * lets the 6-Pack selection survive a panel swap (AC-13). The 6-Pack COUNT is deliberately not
   * here any more: it lives in the theme's stepper and is read off it at click time (see below). */
  const state = {
    variantId: Number(root.dataset.initialVariantId) || 0,
    six: false,
    mode: DEFAULT_MODE
  };

  /* Prices always show ONE 6-Pack (spec §5.3.2) -- the price block, the card and both purchase
   * cards -- exactly as the 3-Pack's $69.99 does not move as you step up. The stepper sets how many
   * go in the cart and nothing else. Liquid still renders n = 2..5; they are never shown, and are
   * left in place rather than edit verified money Liquid for a cosmetic saving. */
  const PRICE_PACKS = 1;

  /* The last selection syncGallery() moved the gallery for (see "The 6-Pack photo" below). */
  let galleryKey = '';

  const msg = (t) => { msgEl.textContent = t || ''; };
  const panelForVariant = (id) => panels.find((p) => Number(p.dataset.threeId) === id) || null;

  /* The Add button is live ONLY when the theme has actually landed on a 3-Pack variant we hold a
   * panel for. This matters because of a window that is easy to miss: checking 6-Pack while a
   * SINGLE is selected calls selectThreePackInNativePicker() and returns, and the theme's variant
   * resolution is ASYNCHRONOUS -- it fetches, then dispatches variant:change. Until that lands,
   * state.variantId is still the Single, panelForVariant() is null, and a click on Add would take
   * the `if (!panel)` branch and do nothing at all, with no message. A shopper who clicks a live
   * button and gets silence concludes the page is broken. Disabling is the honest state, and it is
   * self-clearing: the variant:change handler calls render(), render() calls this.
   *
   * NEVER RE-ENABLED WHILE AN ADD IS IN FLIGHT (`adding`, integration review 2026-09-21). render()
   * ends with this call, and since Round 3 render() also runs on its own whenever Loop's block
   * mutates -- Loop's first render after its CDN fetches, a re-render, the plan guard's edits -- which
   * can land while the Add click is awaiting the cart. Without the guard that render handed back a
   * live button mid-request, and a second click started a second add whose collides() read the SAME
   * not-yet-updated cart: Shopify then merges the two lines into 4 x 3-Pack, twelve filters for a
   * shopper who asked for six. The click's own `finally` clears `adding` and calls this again, so the
   * button still comes back the moment the request settles. */
  function syncAddEnabled() {
    addBtn.disabled = adding || !(state.six && settled(panelForVariant(state.variantId)));
  }

  /* The panel for the variant the theme last FINISHED loading also matches what the native picker
   * currently SAYS. Found in review 2026-09-11, and older than the stepper change: clicking a Filter
   * type checks the new radio synchronously, but state.variantId only moves when the theme's
   * section fetch returns and variant:change fires. In that window a live Add posted the PREVIOUS
   * filter type while the new one showed as selected -- the right count of the wrong product.
   * Requiring the two to agree disables Add for the length of the fetch, and the variant:change
   * render re-enables it.
   *
   * Safe to lean on, and checked rather than assumed: it is the SAME equality render() already
   * uses to press our buttons -- nativeChecked() returns the radio's option-value id, and the
   * panels carry those ids from Liquid (data-pack-value-id / data-type-value-id). If it never held,
   * our buttons would never have shown as pressed, and they were seen working 2026-09-08. */
  function settled(p) {
    return !!p
      && p.dataset.typeValueId === nativeChecked(TYPE_PARAM)
      && p.dataset.packValueId === nativeChecked(PACK_PARAM);
  }

  function currentTypeValueId() {
    if (!TYPE_PARAM) return null;
    const r = document.querySelector(
      'input[type="radio"][name="' + CSS.escape(TYPE_PARAM) + '"]:checked'
    );
    return r ? r.value : null;
  }

  /* Which offer to SHOW. If a 3-Pack is selected it is that variant's panel. If a Single is
   * selected there is no matching panel, but the 6-Pack offer for the current filtration type is
   * still a real, correct offer -- so show it, and let checking it flip the picker to 3-Pack.
   *
   * If the filtration type matches NO panel, return null and let render() hide every panel.
   * *An earlier draft fell back to `panels[0]` here.* That is the identical defect the spec forbids
   * porting from Toby-Rock's `variantOf()`: `panels[0]` is whichever 3-Pack variant Shopify listed
   * first, so a Standard selection would be shown the FLUORIDE panel -- Fluoride prices, Fluoride
   * `data-three-id`, Fluoride plan id -- and checking it would add the wrong filter type. Failing
   * closed is the rule everywhere else in this design and there is no reason for this one function
   * to be the exception: showing nothing is recoverable, showing the wrong filter's money is not. */
  function activePanel() {
    const exact = panelForVariant(state.variantId);
    if (exact) return exact;
    const t = currentTypeValueId();
    return panels.find((p) => p.dataset.typeValueId === t) || null;
  }

  /* ---- Suppressing the controls the 6-Pack supersedes ------------------------------------------
   *
   * SCOPE, exactly: suppression is applied ONLY while the 6-Pack is actively selected, and lifted
   * the instant it is deselected. It is NEVER applied when the 6-Pack fails to render -- the two
   * early returns above (`!root`, `!panels.length`) mean this whole file does nothing in that case,
   * so on an unresolvable allocation `price`, `variant_picker`, `buy_buttons` and the sticky bar all
   * render and work exactly as they do today. That is spec AC-4, and it does not conflict with this
   * suppression: one rule is about an active selection, the other about a control that never
   * appeared. There is no code path that hides a buy control on a page with no 6-Pack on it.
   *
   * WHERE THE ATTRIBUTE LIVES. `data-block-type` sits ON the block wrapper
   * (snippets/product-info.liquid:733), so a CSS descendant combinator such as
   * `.six-pack--active ~ * [data-block-type="@app"]` can never match it -- it looks one level too
   * deep. Act on the wrapper itself.
   *
   * THE MECHANISM IS AN OWNED CLASS, `sp-suppressed`. NOT `hidden`. NOT EVER (D3).
   * assets/market-block.js:81-89 does this, on every variant change:
   *     rerender.querySelectorAll('[data-market-hideable]').forEach(function (el) {
   *       el.hidden = isBlocked;
   *     });
   * and product-info.liquid:733 puts `data-market-hideable` on every @app block wrapper -- so the
   * Loop widget is one of the elements it writes. It is wired to `variant:change` at
   * market-block.js:96 (calling toggleHideableBlocks at :112), and its own comment at :92-94 says
   * the follow-up is DEBOUNCED -- "The debounce ensures we only act once all concurrent server
   * responses have settled". Anything we set synchronously in our own variant:change handler is
   * therefore overwritten by their later write: the Loop widget reappears beside our panel,
   * offering a subscription the shopper cannot act on. `verifySelection()` at :124-146 -- from its
   * `function verifySelection() {` declaration to its closing brace, scheduled at :115-116 on a
   * 150ms timer -- can re-run the whole cycle again, so "re-apply on a timer" is not a fix either.
   * (An earlier draft cited :127-146, three lines inside the function.)
   *
   * `.sp-suppressed { display: none !important; }` is declared in snippets/six-pack-picker.liquid's
   * own <style> (Task 3 Step 2), at the same specificity as the theme's author rule for [hidden]
   * (assets/theme.css:192-194), so it hides identically. Nothing else in the theme writes that
   * class, so there is no race to lose. Do not "simplify" this back to el.hidden.
   *
   * ALL THREE TARGETS GET THE CLASS, not just the Loop widget. Nothing writes `hidden` on
   * buy_buttons or on the sticky bar, so those two would work either way -- but one mechanism is
   * one thing to verify, and a mixed design is what gets unified back onto `hidden` by the next
   * person reading only half of this comment.
   *
   * THREE CONTROLS, three reasons:
   *   - the Loop widget, because it renders PER-UNIT prices beside a 6-Pack that must show line
   *     totals (spec section 5.3, spec AC-17);
   *   - the buy_buttons block, because under D1 the theme's own Add to Cart stays live and would
   *     otherwise add ONE 3-Pack at $69.99 to a shopper who believes they selected a 6-Pack;
   *   - the STICKY add-to-cart bar, for the same reason and more urgently. Its two buttons are
   *     `type="submit" form="<product_form_id>"` (snippets/product-sticky-add-to-cart.liquid:57-58)
   *     -- the SAME form buy_buttons submits, so they add the same one 3-Pack. The outline button
   *     at :57 carries `class: 'sm-max:hidden'` (desktop) and the solid one at :58 carries
   *     `class: 'sm:hidden'` (mobile), so on mobile this bar IS the primary buy control.
   *     `templates/product.mina-filter.json:227` sets `show_sticky_add_to_cart: true`, so it is live
   *     on this PDP. Suppressing buy_buttons and not this leaves the hazard wide open on the
   *     surface where it is most likely to be hit, while reading as closed.
   *
   * WHY TWO ROOTS. The block wrappers live inside the main `<product-rerender>`
   * (sections/main-product.liquid:50-74). The sticky bar is rendered at
   * sections/main-product.liquid:78, OUTSIDE that element -- so `host`, which is
   * `root.closest('product-rerender')`, can never reach it. Query the sticky bar from `document`.
   *
   * WHICH NODE FOR THE STICKY BAR. `<product-sticky-add-to-cart>` itself, never the
   * `<product-rerender>` inside it: that inner element has no `allow-partial-rerender`, so
   * assets/theme.js:3681-3682 `replaceWith()`s the entire node on every variant change and any state
   * remembered on it is gone. The custom element wrapping it survives.
   *
   * RESTORE ONLY WHAT WE SUPPRESSED. Each target we hide is marked `data-sp-suppressed="1"`, and
   * only a marked element has the class removed. Because we never touch `hidden`, the market's own
   * hiding is untouched throughout: snippets/product-info.liquid:733 writes `hidden` on a
   * market-blocked app block and assets/market-block.js:86-88 keeps writing it, and deselecting the
   * 6-Pack cannot reveal such a block because we never cleared anything. That is the whole
   * bookkeeping problem the class dissolves -- the earlier `hidden`-based version had to record and
   * restore a prior attribute value and could still be raced while doing it.
   *
   * THE WRAPPERS SURVIVE THE PARTIAL RERENDER. All three do, and the reasons differ:
   *   - buy_buttons IS in the :721 rerender list, but assets/theme.js:3688-3695 gives "buy-buttons"
   *     its OWN branch that replaces only the inner <buy-buttons> element and copies the quantity
   *     across (:3691). The generic `element.replaceWith(matchingBlock)` at :3700 is the else branch
   *     and does not run for this type, so the WRAPPER -- which is what carries our class and our
   *     marker -- is never touched. *An earlier draft of this plan said the buy_buttons wrapper IS
   *     replaced on a variant change. It is not; the spec has this right and this file now matches
   *     it.*
   *   - the Loop @app block is not in the :721 list at all, so theme.js:3684's selector never
   *     matches its wrapper;
   *   - <product-sticky-add-to-cart> is outside the rerendered subtree entirely (see WHY TWO ROOTS).
   * So the re-assertion on variant:change below is belt-and-braces against a theme upgrade, not the
   * mechanism that makes suppression stick. It is cheap and idempotent; keep it.
   */
  const host = root.closest('product-rerender') || document;

  /* The Loop widget, identified DETERMINISTICALLY. product-info.liquid:733 emits
   * data-block-id="{{ block.id }}" on every block wrapper, and this block's key in
   * templates/product.mina-filter.json:72 is `loop_subscriptions_loop_widget_Pa7778` -- so the id
   * carries the app's name and no content sniffing is required. The sniff below is kept only as a
   * fallback for the day that key is regenerated, and it runs only when the deterministic selector
   * finds nothing. */
  const LOOP_BY_ID = '[data-block-type="@app"][data-block-id*="loop_subscriptions"]';
  const LOOP_SNIFF = '[class*="loop" i], [id*="loop" i], [data-loop], loop-widget';

  function suppressible() {
    const out = [];

    host.querySelectorAll(LOOP_BY_ID).forEach((el) => out.push(el));
    if (!out.length) {
      host.querySelectorAll('[data-block-type="@app"]').forEach((el) => {
        if (el.querySelector(LOOP_SNIFF)) out.push(el);
      });
    }

    host.querySelectorAll('[data-block-type="buy-buttons"]').forEach((el) => out.push(el));

    /* From `document`, deliberately -- see WHY TWO ROOTS above. */
    document.querySelectorAll('product-sticky-add-to-cart').forEach((el) => out.push(el));

    return out;
  }

  /* THE SUPPRESSION CLASS this design owns. Declared in snippets/six-pack-picker.liquid's <style> as
   * `.sp-suppressed { display: none !important; }`. See D3 and the comment above: `hidden` is
   * written by assets/market-block.js:86-88 on a debounced variant:change listener and cannot be
   * used here. */
  const SUPPRESSED = 'sp-suppressed';

  /* The other class this design owns: the sale colour our copy of the top price wears whenever there
   * is a struck figure beside it (Sanjeet, 2026-09-22 -- see swapPrice). Declared in
   * snippets/six-pack-picker.liquid's <style> as
   * `[data-sp-price] .six-pack__price--on-sale { color: rgb(var(--on-sale-text)) }`, the theme's own
   * on-sale token. Never the theme's `text-on-sale`: that name is price-list.liquid's to choose. */
  const ON_SALE = 'six-pack__price--on-sale';

  /* The native variant_picker is hidden whenever THIS control rendered -- unconditionally, not only
   * while 6-Pack is selected, because our three buttons replace it outright. This REVERSES D1's
   * "the native picker is never suppressed", decided 2026-09-04 against an approved mockup.
   *
   * It is safe for exactly the reason D1 cared about. v3 suppressed from LIQUID, unconditionally,
   * while rendering its replacement only inside the readiness gate -- so a failed gate left the PDP
   * with no buy control at all. This hide lives in script that only runs because the gated snippet
   * rendered and loaded it. Gate fails -> no markup -> no script -> picker untouched. The coupling
   * is structural, not a second copy of the gate condition that could drift.
   *
   * Re-asserted every render: variant_picker IS in block_types_allowing_partial_rerender
   * (product-info.liquid:725), so it is replaced wholesale on variant change and a one-time hide
   * would be undone by the first Filtration Type click. Never removed -- there is no state in which
   * our control is present and the native picker should return. */
  function suppressNativePicker() {
    host.querySelectorAll('[data-block-type="variant-picker"]').forEach((el) => {
      if (el.classList.contains(SUPPRESSED)) return;
      el.classList.add(SUPPRESSED);
      el.dataset.spSuppressed = '1';
    });
  }

  function applyBlockSuppression(on) {
    suppressible().forEach((el) => {
      if (on) {
        if (el.classList.contains(SUPPRESSED)) return;
        el.classList.add(SUPPRESSED);
        el.dataset.spSuppressed = '1';
        return;
      }
      /* Remove only what WE added. A class that arrived some other way is not ours to clear. */
      if (el.dataset.spSuppressed !== '1') return;
      el.classList.remove(SUPPRESSED);
      delete el.dataset.spSuppressed;
    });
  }

  /* ---- The theme's quantity stepper ------------------------------------------------------------
   * Ours was removed 2026-09-11 (spec §5.3.2, item 2). While 6-Pack is selected the theme's own
   * stepper in the price block counts 6-PACKS, and Add sends twice that many 3-Packs.
   *
   * READ AT CLICK TIME, off the element the shopper can see -- never from remembered state. So what
   * is added is always what is displayed: if anything below fails to carry the count across a
   * Filtration Type change, the stepper simply reads 1 and one 6-Pack is added, which is the cheap
   * direction to be wrong in. No stepper at all (a template with show_qty_input off) also means 1. */
  const themeStepper = () => host.querySelector('[data-block-type="price"] quantity-selector');

  function sixPackCount() {
    const el = themeStepper();
    const input = el && el.querySelector('input');
    const n = input ? parseInt(input.value, 10) : NaN;
    return n > 0 ? n : 1;
  }

  /* Set through the component's OWN public setter, QuantitySelector.quantity (assets/theme.js,
   * `set quantity`), which writes the input, fires a bubbling `change` and refreshes its ± buttons
   * -- precisely what a ± click does, so nothing downstream sees anything unusual. Never by poking
   * input.value, which would leave the component's ± state stale. `'quantity' in el` is false until
   * the custom element has upgraded; the caller simply tries again on the next render(). */
  function setCount(el, n) {
    if (!el || !('quantity' in el)) return false;
    const input = el.querySelector('input');
    const max = input && input.max ? parseInt(input.max, 10) : NaN;
    el.quantity = max > 0 ? Math.min(n, max) : n;
    return true;
  }

  /* AC-13: the count survives a Filtration Type change. The theme REBUILDS the price block --
   * stepper and all -- on every variant change, so the stepper comes back at 1. The Filter type row
   * is ours, so we know a change is coming: snapshot on that click, and restore once a DIFFERENT
   * stepper element is on the page.
   *
   * Compared by ELEMENT, not by timing, and that is what makes it safe. render() also runs
   * synchronously on the click itself, before the rebuild, and must not spend the snapshot on the
   * old stepper that is about to be thrown away. Scoped to 6-Pack mode: a 3-Pack keeps the theme's
   * existing reset, which is not ours to change. */
  let pendingCount = null;

  function restoreCount() {
    if (!pendingCount) return;
    if (!state.six) { pendingCount = null; return; }
    const el = themeStepper();
    if (!el || el === pendingCount.from) return;
    if (pendingCount.value <= 1 || setCount(el, pendingCount.value)) pendingCount = null;
  }

  /* The theme's Add to Cart takes its weight from `buy-buttons--weight-*` on its <buy-buttons>
   * wrapper (theme.css:7949-7959), set by the buy_buttons block's button_font_weight. Ours has no
   * such wrapper, so copy the class off the theme's own element -- hidden while 6-Pack is
   * selected, but present -- rather than hardcode a weight that drifts from the setting. `host`
   * scoping keeps the sticky bar's copy out of it. */
  function syncButtonWeight() {
    const bb = host.querySelector('buy-buttons');
    if (!bb || !addWrap) return;
    bb.classList.forEach((c) => { if (/^buy-buttons--weight-/.test(c)) addWrap.classList.add(c); });
  }

  /* The label follows the purchase mode, as the 3-Pack's does; both strings come from Liquid.
   * ⚠️ The store's button_animation is "up", which copies the label into an `up-text` attribute
   * that the hover effect reads through CSS -- change the text alone and hovering shows the OLD
   * words. So update both, and `rotated-text` too, for a store that switches animation. The
   * loading state's shadow root keeps a <slot> for this text, so it survives an add. */
  function syncButtonLabel() {
    const label = state.mode === 'subscribe' ? actions.dataset.labelSubscribe : actions.dataset.labelOnetime;
    if (!label) return;
    const target = addBtn.querySelector('button-content') || addBtn;
    if (target.textContent.trim() === label) return;
    target.textContent = label;
    ['up-text', 'rotated-text'].forEach((a) => { if (target.hasAttribute(a)) target.setAttribute(a, label); });
  }

  /* aria-busy is cleared only once the button can animate back. assets/theme.js's ButtonContent
   * runs its finish-loading animation ONLY while its button is visible (`if
   * (!this?.checkVisibility()) return;`), so clearing it while the 6-Pack actions are hidden --
   * the shopper left 6-Pack mid-request -- would leave the loading dots up and no label the next
   * time 6-Pack is chosen. Found in review 2026-09-11. render() retries, so it clears the moment
   * the button is visible again; `adding` stops that retry from cutting a live request short. */
  let adding = false;
  function clearBusy() {
    if (adding || !addBtn.hasAttribute('aria-busy')) return;
    if (typeof addBtn.checkVisibility === 'function' && !addBtn.checkVisibility()) return;
    addBtn.removeAttribute('aria-busy');
  }

  /* The theme's own price block. Decided 2026-09-09 (Sanjeet): with 6-Pack chosen the page must show
   * the 6-PACK's money -- $124.60 struck from $155.94 -- not the 3-Pack's $69.99. Until then the price
   * block was left exactly as it was, so the page carried two different prices at once and only one
   * of them was what the shopper was about to pay.
   *
   * ROUND 3 EXTENDS IT TO EVERY PACK SIZE (spec 5.3.4 R3-11, R3-7). The top price now follows the one
   * shared One-time / Subscribe choice for Single and 3-Pack too -- a subscribed 3-Pack reads $66.49
   * struck from $77.97 -- and carries a "Save X%" badge whenever something is struck. So this now
   * runs whenever our control is on the page, not only for the 6-Pack: with one-time selected the
   * copy shows the same strings the theme would (the variant price, its compare-at) plus the badge.
   * The caller hands us the three strings; see topFigure() below for where each comes from.
   *
   * WE DO NOT WRITE INTO THE THEME'S PRICE ELEMENTS, and that is the whole design of this function.
   * snippets/price-list.liquid:64 and :82 stamp `data-fly-bundles-native-discounted-price` and
   * `data-fly-bundles-native-original-price` onto <sale-price> and <compare-at-price>. Those
   * attributes exist to tell Fly Bundles which nodes to rewrite, and Fly IS live on this page (31
   * offer codes in the served HTML). No offer targets MINA today, so it is dormant rather than
   * absent -- exactly the shape of the `hidden` race with market-block.js that forced D7's
   * owned-class rule. Writing our money into a node another app is wired to overwrite would be that
   * same mistake with a different app. So: hide THEIR <price-list> with the class we own, and put
   * OUR element beside it. The quantity stepper is a SIBLING of <price-list>
   * (product-info.liquid:171-174), so it is untouched and keeps working.
   *
   * THE SCRIPT STILL NEVER AUTHORS A MONEY STRING -- §5.6. Liquid renders every figure up front --
   * money AND the "Save X%" text, whose percentage Liquid computes and rounds down -- and this only
   * copies strings. There is no arithmetic here, no currency formatting, and no second copy of the
   * pricing rules that could drift from the cards sitting inches below it.
   *
   * WHERE THOSE FIGURES LIVE. Never in anything visible: the 6-Pack's in a `.six-pack__swap-src`
   * wrapper in its panel, the Single's and 3-Pack's in one per variant after the purchase cards.
   * Both are read with textContent (unlike innerText it reads a node inside a display:none wrapper)
   * and selected by their own attributes, never by whether they are rendered. Do not switch either
   * to a rendering-aware check, or the top price silently falls back to the theme's.
   *
   * FAILS OPEN. No block, no <price-list>, or no figure to copy => leave the theme's price exactly as
   * it was. A page showing the theme's price is merely the old behaviour; a page showing a blank
   * where its price used to be is a broken page.
   *
   * The work is done by swapPrice() below, which the sticky bar's price shares (applyStickyPrice). */
  function applyPriceSwap(fig) {
    swapPrice(host.querySelector('[data-block-type="price"]'), fig, null);
  }

  /* THE STICKY ADD-TO-CART BAR'S PRICE (integration review 2026-09-21). Since R3-11 the top price shows
   * a subscribed Single or 3-Pack at its subscribed price, while the sticky bar -- the same product
   * form's buttons, so it adds that same subscription -- still said the one-time price. It now follows,
   * through swapPrice() and the SAME Liquid strings as the top price, so the two cannot disagree.
   *
   * WHY IT IS SAFE, checked against snippets/product-sticky-add-to-cart.liquid and price-list.liquid:
   *   - its <price-list> is rendered with context 'line_item', which stamps NO data-fly-bundles-*
   *     attribute, and we do not write into it anyway: it is hidden with our owned class and our copy
   *     goes beside it, exactly as in the price block;
   *   - it sits inside the sticky bar's <product-rerender>, which has no allow-partial-rerender, so
   *     assets/theme.js replaceWith()s the whole subtree on every variant change -- our copy and our
   *     class leave with it and the theme's fresh price shows until the render() that follows
   *     variant:change re-applies this, the same one-frame gap the price block has. Nothing is cached:
   *     it is looked up from `document` on every call (it is outside `host`; see WHY TWO ROOTS);
   *   - no bar, no <price-list> or no figure => the theme's own price, untouched.
   *
   * SCOPE. Only a SUBSCRIBED Single or 3-Pack is swapped. One-time, the theme's own figures are the
   * right ones, so they are restored rather than copied; with 6-Pack selected the whole bar is
   * suppressed (applyBlockSuppression) and its price restored underneath. NO "Save X%" badge: R3-7
   * puts that beside the top price, and this is a compact bar -- the badge is never passed here. The
   * bar's price is the theme's desktop-only row (`sm-max:hidden`); on a phone the bar shows no price.
   *
   * A subscribed SINGLE strikes its one-time price (see topFigure), but in this bar the theme rendered
   * no <compare-at-price> for it -- line_item context draws one only for a variant on sale -- so there
   * is no screen-reader "Regular price" label to copy. It is borrowed from the theme's own
   * <compare-at-price> in the price block, which context 'product' always renders; the same theme
   * translation, never text of ours. */
  function applyStickyPrice(fig) {
    const sticky = document.querySelector('product-sticky-add-to-cart');
    const mainWas = host.querySelector('[data-block-type="price"] price-list compare-at-price');
    swapPrice(sticky, fig && fig.now ? { now: fig.now, was: fig.was, save: '' } : null, mainWas);
  }

  /* Copy `fig` over the theme's <price-list> inside `scope`, or restore the theme's when `fig` has no
   * price. `wasLabelFrom` is a theme <compare-at-price> to borrow the struck figure's screen-reader
   * label from when `scope` has none of its own. */
  function swapPrice(scope, fig, wasLabelFrom) {
    const list = scope && scope.querySelector('price-list');
    if (!list) return;

    let ours = scope.querySelector('[data-sp-price]');

    /* Restore covers "no figure for this selection" and "we could not read a figure" with one path,
     * so a half-applied swap -- ours present, theirs hidden, nothing to show -- cannot survive a
     * render. */
    if (!fig || !fig.now) {
      if (ours) ours.remove();
      if (list.dataset.spSuppressed === '1') {
        list.classList.remove(SUPPRESSED);
        delete list.dataset.spSuppressed;
      }
      return;
    }

    const now = fig.now;
    const was = fig.was || '';
    /* A saving is only ever claimed against a figure the shopper can SEE struck (R3-7). */
    const save = was ? (fig.save || '') : '';

    /* THE SAME MARKUP AS THE THEME'S OWN PRICE -- a `.price-list` wrapper holding <sale-price> and
     * <compare-at-price> -- so theme.css:7827-7829 (body font, 28px, text weight) and
     * theme.css:4864 (the layout) apply to ours exactly as they do to the theme's. *The first build
     * used a <div> with plain <span>/<s> children; that rule matches only those tag names inside
     * .price-list, so it missed, and the copied h6 class rendered the figure in the HEADING font --
     * a visibly serif price on a sans-serif page (spec §5.3.2, item 1).*
     *
     * Reusing the TAG names is safe, and was checked rather than assumed: neither is a registered
     * custom element (no customElements.define in theme.js, and theme.js queries neither), and Fly
     * finds price nodes by ATTRIBUTE -- `[data-fly-bundles-native-discounted-price='true']` --
     * never by tag. Ours carry no data-fly-bundles-* attribute, so Fly still cannot see them. That
     * attribute, not the tag, is the thing this function must never copy.
     *
     * The wrapper is a <div class="price-list">, not a <price-list>, so that `querySelector('price-list')`
     * above keeps finding only the theme's -- which is what the restore path depends on. */
    if (!ours) {
      ours = document.createElement('div');
      ours.setAttribute('data-sp-price', '');
      /* The theme's own classes (price-list plus its alignment), minus the suppression class we
       * add to it -- copying that across would hide ours too. */
      ours.className = Array.from(list.classList).filter((c) => c !== SUPPRESSED).join(' ');
      list.parentNode.insertBefore(ours, list.nextSibling);
    }

    /* Rebuild only when the strings actually change. render() runs on every click and every
     * variant:change; replacing identical nodes each time would restart CSS transitions and churn
     * the accessibility tree for no reason. */
    if (ours.dataset.spVal !== now + '|' + was + '|' + save) {
      ours.dataset.spVal = now + '|' + was + '|' + save;
      ours.textContent = '';

      /* Classes COPIED off the theme's own elements, never guessed: price-list.liquid builds them
       * from section settings (on_sale_price_classes -> colour, compare_at_price_classes ->
       * line-through), so a hardcoded guess would drift the moment a merchant changes one. */
      const themeNow = list.querySelector('sale-price');
      const themeWas = list.querySelector('compare-at-price');

      /* The theme's price names each figure for screen readers -- a visually hidden "Sale price" /
       * "Regular price" inside each element (price-list.liquid:64-65, :82-83). Without it a screen
       * reader hears two bare amounts and cannot tell which one is paid. Clone ONLY that label,
       * never the element itself, which would carry its data-fly-bundles-* attribute across and
       * hand our node to Fly. Found in review 2026-09-11. */
      const srLabel = (src) => {
        const l = src && src.querySelector('.sr-only');
        return l ? l.cloneNode(true) : null;
      };

      const nowEl = document.createElement('sale-price');
      nowEl.className = themeNow ? themeNow.className : '';
      /* A STRUCK FIGURE BESIDE IT MEANS SALE STYLING, EVERY PACK SIZE (Sanjeet, 2026-09-22). The
       * classes copied on the line above are the ones the theme chose FOR THE VARIANT, and it marks a
       * price on-sale only where that variant's compare-at is above its price (price-list.liquid:64).
       * The MINA 3-Pack's is ($69.99 from $77.97); the Single's is not ($25.99 from $25.99). So with
       * Subscribe chosen the Single showed $24.69 struck from $25.99 in the theme's REGULAR colour
       * while the 3-Pack and the 6-Pack showed theirs in the sale colour -- one control, one kind of
       * saving, two colours, decided by a compare-at the shopper is not being shown. This copy is not
       * showing the variant, it is showing the SELECTION, so the rule is ours and it is uniform.
       *
       * Keyed on `was` -- the struck figure we are about to append below -- and on nothing else, so
       * the colour and the strike can never disagree, and a state with nothing struck (a one-time
       * Single) is left exactly as the theme rendered it. `was` is part of the spVal key above, so
       * the rebuild guard already covers it.
       *
       * OUR OWN CLASS, never the theme's `text-on-sale`: that string is price-list.liquid's to choose,
       * and hardcoding a copy of it here is the drift the class-copying above exists to avoid. The
       * stylesheet gives ours the theme's own --on-sale-text token, so a merchant changing the accent
       * moves ours with it. It goes on the element WE built; the theme's <sale-price> is untouched,
       * which is the whole point of this function. No money and no percentage is read or written here
       * (§5.6) -- only whether Liquid handed us something to strike.
       *
       * FAILS OPEN: with the class missing the figure is the theme's colour, which is what it is
       * today. Wrong, not broken. */
      if (was) nowEl.classList.add(ON_SALE);
      const nowLbl = srLabel(themeNow);
      if (nowLbl) nowEl.appendChild(nowLbl);
      nowEl.appendChild(document.createTextNode(now));
      ours.appendChild(nowEl);

      if (was) {
        const wasEl = document.createElement('compare-at-price');
        /* No theme <compare-at-price> here -- the sticky bar's line_item price draws one only for a
         * variant on sale -- means its <sale-price> carries the REGULAR classes, and in line_item (and
         * every card style) price-list.liquid's compare-at classes are exactly those plus
         * `line-through` ('subheading text-subdued' -> 'subheading text-subdued line-through'). So
         * derive them the same way from the element that is there. Context 'product' -- the price
         * block -- is the one that differs (h6 / h7), and it always draws its own, so never gets here. */
        wasEl.className = themeWas
          ? themeWas.className
          : [themeNow && themeNow.className.trim(), 'line-through'].filter(Boolean).join(' ');
        const wasLbl = srLabel(themeWas) || srLabel(wasLabelFrom);
        if (wasLbl) wasEl.appendChild(wasLbl);
        wasEl.appendChild(document.createTextNode(was));
        ours.appendChild(wasEl);
      }

      /* "Save X%" (R3-7), our own class and never a theme element -- styled in the snippet's
       * stylesheet. A plain text node: it is words the shopper reads after the two prices, and the
       * two prices already carry the theme's screen-reader labels. */
      if (save) {
        const saveEl = document.createElement('span');
        saveEl.className = 'six-pack__save';
        saveEl.textContent = save;
        ours.appendChild(saveEl);
      }
    }

    if (list.dataset.spSuppressed !== '1') {
      list.classList.add(SUPPRESSED);
      list.dataset.spSuppressed = '1';
    }
  }

  /* ---- Round 3: one purchase choice for every pack size (spec 5.3.4) ------------------------------
   *
   * THE CHOICE. One-time or Subscribe is ONE choice shared by all three pack sizes (R3-10): switching
   * pack keeps it, and every pack cell, the top price, its badge and the callout show the money for
   * it. It lives in two places, because the page has two sets of purchase cards:
   *   - 6-Pack selected: OUR fieldset, so it is state.mode;
   *   - Single or 3-Pack selected: LOOP'S widget, so it is whatever Loop has selected, READ off Loop's
   *     markup every time (never cached -- Loop re-renders its widget on every variant change).
   * Moving between the two carries it across: into the 6-Pack, our radio takes Loop's choice (the
   * click handler); out of it, Loop is set to ours through Loop's own click path (applyLoopIntent).
   *
   * WHERE IT STARTS (R3-12, Sanjeet 2026-09-22): at DEFAULT_MODE -- Subscribe -- on all three pack
   * sizes. For the 6-Pack that is the Liquid's `checked`; for the Single and 3-Pack the arrival
   * intent at the foot of this file asks Loop for it. See both for the reasoning.
   *
   * FAILS SAFE TO ONE-TIME. Whatever cannot be read -- Loop not rendered yet, no subscription on the
   * variant, Loop's markup and its hidden input disagreeing, a plan we hold no price for -- shows the
   * one-time figures. Those are the theme's own figures, so the failure is the page as it was. That
   * is unchanged by R3-12: the default only asks Loop to SELECT Subscribe. Until Loop's own markup
   * and its hidden selling_plan input both say so, and say so with the plan our figure was rendered
   * for, every figure on the page is still the one-time one (displayMode()).
   *
   * WHAT IS READ FROM LOOP, and why each (loop_widget.js, extension loop-subscriptions-261, read
   * 2026-09-21):
   *   - the selected option: Loop's click handlers (handleLoopWidgetSellingPlanGroupChange /
   *     handleLoopWidgetOnetimeClick) put `loop-widget-purchase-option-selected` on it and set
   *     aria-checked="true" through updateRadioStates. Subscribe options carry
   *     [data-loop-widget-selling-plan-group], one-time [data-loop-widget-onetime-purchase-option];
   *   - the hidden `selling_plan` input Loop writes into the product form in the same handlers
   *     (handleLoopWidgetSellingPlanValue) -- the value the CART actually receives. Loop's widget sits
   *     OUTSIDE that form, so the form is found through the native picker's radios (`form=`).
   * Subscribe is believed only when both agree (a subscribe option selected AND a plan id posted), and
   * one-time only when both agree the other way. */

  const figText = (el) => (el ? el.textContent.trim() : '');
  const vsrcFor = (variantId) => vsrcs.find((s) => Number(s.dataset.spVariantId) === variantId) || null;

  /* Loop's block wrappers: the same deterministic id, then the same sniff, as suppressible() uses. */
  function loopBlocks() {
    const byId = Array.from(host.querySelectorAll(LOOP_BY_ID));
    if (byId.length) return byId;
    return Array.from(host.querySelectorAll('[data-block-type="@app"]')).filter((el) => el.querySelector(LOOP_SNIFF));
  }

  /* Loop's widget container. Queried FRESH on every call: Loop replaces everything inside it on each
   * render, and the theme may rebuild around it. Its own id is `loop-widget-container-id-<product>`. */
  function loopBox() {
    for (const w of loopBlocks()) {
      const box = w.querySelector('.loop-widget-container');
      if (box) return box;
    }
    return host.querySelector('[id^="loop-widget-container-id-"]');
  }

  function planInput() {
    if (!PACK_PARAM) return null;
    const r = document.querySelector('input[type="radio"][name="' + CSS.escape(PACK_PARAM) + '"]');
    const form = r && r.form;
    return form ? form.querySelector('input[name="selling_plan"]') : null;
  }

  const loopPicked = (el) => !!el &&
    (el.classList.contains('loop-widget-purchase-option-selected') || el.getAttribute('aria-checked') === 'true');

  /* { box, wrap, hasSub, mode, planId }. mode is 'subscribe', 'onetime', or null for "cannot tell". */
  function readLoop() {
    const out = { box: loopBox(), wrap: null, hasSub: false, mode: null, planId: '' };
    const box = out.box;
    if (!box) return out;
    out.wrap = box.closest('[data-block-type]');
    if (box.classList.contains('loop-display-none')) return out;
    const subs = Array.from(box.querySelectorAll('[data-loop-widget-selling-plan-group]'));
    out.hasSub = subs.length > 0;
    if (!out.hasSub) return out;
    const subOn = subs.some(loopPicked);
    const oneOn = loopPicked(box.querySelector('[data-loop-widget-onetime-purchase-option]'));
    const input = planInput();
    const plan = input ? String(input.value || '').trim() : '';
    if (subOn && !oneOn && plan) { out.mode = 'subscribe'; out.planId = plan; }
    else if (oneOn && !subOn && !plan) out.mode = 'onetime';
    return out;
  }

  /* Loop's own record of the variant it last rendered, when it exposes one (window.LOOP_WIDGET is
   * Loop's global, keyed by product id). Used ONLY to wait: if it names another variant, Loop has not
   * re-rendered yet. Absent or unreadable means "no objection". */
  function loopRenderedFor(box, variantId) {
    const m = /^loop-widget-container-id-(\d+)$/.exec(box.id || '');
    const lw = m && window.LOOP_WIDGET && window.LOOP_WIDGET[m[1]];
    const v = lw ? Number(lw.selectedVariantId) : 0;
    return !v || v === variantId;
  }

  /* The mode EVERY figure on the page shows. For Single / 3-Pack, subscribed only when Loop says
   * Subscribe AND the plan Loop is posting is the plan our figure was rendered for -- so the page
   * never prices one plan while the cart receives another. */
  function displayMode() {
    if (state.six) return state.mode;
    const L = readLoop();
    if (L.mode !== 'subscribe') return 'onetime';
    const src = vsrcFor(state.variantId);
    return src && src.dataset.spPlanId && src.dataset.spPlanId === L.planId ? 'subscribe' : 'onetime';
  }

  /* The three strings for the top price, all Liquid-rendered. 6-Pack: the panel's swap source, whose
   * [data-sp-fig] render() has just unhidden for state.mode. Single / 3-Pack: the variant the THEME
   * last loaded (state.variantId) -- the variant its own price block is showing -- so during a
   * variant fetch ours and the theme's describe the same variant.
   *
   * The struck figure is PER MODE for Single / 3-Pack (Round 3 integration, 2026-09-21): the
   * compare-at if it is above the price, else the one-time price, shown whenever it is greater than
   * the price the mode displays -- so a subscribed Single strikes its one-time $25.99, as Loop's own
   * Single card does. Liquid decides all of that and renders a [data-sp-was] only for a mode that
   * strikes something; this only picks the mode's span. */
  function topFigure(panel, mode) {
    if (state.six) {
      if (!panel) return null;
      return {
        now: figText(panel.querySelector('[data-sp-fig]:not([hidden])')),
        /* PER MODE since 2026-09-22 (see the Liquid): the reference figure is emitted only for a mode
         * where it is actually above the price, so there may be one, two or none. Keyed BOTH ways --
         * the attribute here and the hidden flag render() sets -- so neither alone is load-bearing. */
        was: figText(panel.querySelector('[data-sp-ref-fig][data-mode="' + mode + '"]:not([hidden])')),
        save: figText(panel.querySelector('[data-sp-save][data-mode="' + mode + '"]'))
      };
    }
    const src = vsrcFor(state.variantId);
    if (!src) return null;
    return {
      now: figText(src.querySelector('[data-sp-now][data-mode="' + mode + '"]')),
      was: figText(src.querySelector('[data-sp-was][data-mode="' + mode + '"]')),
      save: figText(src.querySelector('[data-sp-save][data-mode="' + mode + '"]'))
    };
  }

  /* ONE subscribe fig for this cell's button and filter type exists, so Subscribe can show it. */
  function hasSubscribeFig(el) {
    const btn = el.closest('[data-sp-pack]');
    return cellFigs.some((o) => o.dataset.mode === 'subscribe'
      && o.dataset.spForType === el.dataset.spForType
      && o.closest('[data-sp-pack]') === btn);
  }

  /* SETTING LOOP'S CHOICE when the shopper arrives at Single / 3-Pack (R3-10) -- through Loop's OWN
   * click path: a click on Loop's option element runs Loop's own listener, which updates its state,
   * its markup and the hidden selling_plan input together. Nothing of Loop's is written directly.
   *
   * It must land on Loop's rendering FOR THE NEW VARIANT. 6-Pack -> 3-Pack keeps the variant, so the
   * rendering on the page already is that; 6-Pack or 3-Pack -> Single changes it, and Loop rebuilds its
   * widget after the theme's variant fetch. So the intent is kept until Loop, rendered for the target
   * variant, shows the intended option -- clicking the rendering in front of it meanwhile, which Loop
   * carries across its own re-render (it remembers selectedSellingPlanGroupId / previousPurchaseOption),
   * so the shopper does not watch the prices flip back and forth while the fetch runs.
   *
   * CANNOT LOOP: at most ONE click per Loop option element (`clicked`), and Loop only makes new
   * elements when it re-renders; the intent expires LOOP_INTENT_MS after Loop first becomes readable
   * (see the note on the deadline below -- while Loop is unreadable nothing is clicked at all, so
   * the unstarted clock bounds nothing that is running); and a real click of the
   * shopper's inside Loop's widget cancels it, so we never fight the shopper. Every call site is a
   * render(), so a click here never re-enters itself synchronously (Loop's mutations reach us through
   * a MutationObserver, which is asynchronous). */
  /* THE DEADLINE RUNS FROM WHEN LOOP IS THERE, not from when the intent was made (`until` starts at
   * 0 and applyLoopIntent sets it on the first pass that can actually read Loop). It is a budget for
   * "keep trying on the widget in front of us", and waiting for the widget to EXIST is not trying.
   *
   * Corrected 2026-09-22, found by the R3-12 harness: measured from queue time, four seconds is
   * shorter than a cold load of Loop's widget on a throttled connection (assets/loop-plan-guard.js
   * records the same fact -- "no id readable is the NORMAL state for the first frames of every page
   * load" -- and assets/component-purchase-picker.js already waits 40 x 50ms for it). A shopper who
   * arrived and pressed a pack or filter button before Loop had drawn anything re-queued the intent,
   * the new deadline expired while Loop was still fetching, and the page's Subscribe default was
   * silently lost -- the page then sat on One-time having never asked.
   *
   * Nothing is fighting the intent while it waits: until Loop draws a purchase option there is
   * nothing in it for the shopper to click, and the first thing they DO click either cancels it
   * (yieldIntentToShopper) or replaces it (the pack and filter handlers). Entering the 6-Pack drops
   * it. So an intent that never fires is inert, not stale. */
  const LOOP_INTENT_MS = 4000;
  let loopIntent = null;

  function queueLoopIntent(mode, variantId) {
    loopIntent = (mode === 'subscribe' || mode === 'onetime') && variantId
      ? { mode, variantId, until: 0, clicked: new WeakSet() }
      : null;
  }

  /* THE SHARED CHOICE AS IT STANDS, for carrying across a pack or filter change. Loop's own answer
   * first; then, only while Loop has not answered yet, the intent already in flight.
   *
   * That second fallback is R3-12's doing. Before it, a shopper who changed pack or filtration type
   * in the seconds BEFORE Loop's widget finished loading read null off Loop, and queueLoopIntent(null)
   * threw the arrival intent away -- so arriving and immediately clicking "Standard" silently lost the
   * page's Subscribe default. An intent is a choice that has been made and not yet landed, so it
   * carries like one.
   *
   * null means "nothing to carry", which queues nothing and changes nothing -- it never invents a
   * choice. displayMode() does NOT use this: what is DISPLAYED still follows Loop's markup alone, so
   * the page can never price an option Loop has not actually selected. */
  function currentChoice() {
    if (state.six) return state.mode;
    return readLoop().mode || (loopIntent ? loopIntent.mode : null);
  }

  function applyLoopIntent() {
    const it = loopIntent;
    if (!it) return;
    if (state.six) { loopIntent = null; return; }
    const L = readLoop();
    if (!L.box || !L.hasSub) return;            // Loop not rendered (yet): wait, and do not start the clock
    if (!it.until) it.until = Date.now() + LOOP_INTENT_MS;
    else if (Date.now() > it.until) { loopIntent = null; return; }
    const onTarget = state.variantId === it.variantId && loopRenderedFor(L.box, it.variantId);
    if (L.mode === it.mode) { if (onTarget) loopIntent = null; return; }
    const opt = it.mode === 'subscribe'
      ? L.box.querySelector('[data-loop-widget-selling-plan-group]')
      : L.box.querySelector('[data-loop-widget-onetime-purchase-option]');
    if (!opt || it.clicked.has(opt)) return;
    it.clicked.add(opt);
    opt.click();
    if (onTarget && readLoop().mode === it.mode) loopIntent = null;
  }

  /* R3-9: on the MINA page Loop's Subscribe card strikes the COMPARE-AT, as the top price and our
   * 6-Pack card do, instead of Loop's default of the one-time price. Loop's own `showCompareAtPrice`
   * would do it store-wide and was rejected, so the text of Loop's struck element is swapped here --
   * for a Liquid-rendered string, never one this script formats.
   *
   * The figure is the variant's SUBSCRIBE-mode struck span -- the one the top price strikes while
   * Subscribe is picked. On a 3-Pack that is the compare-at; on the Single it is the one-time price,
   * which is already what Loop strikes, so the equality check below finds nothing to change.
   *
   * Only when ALL hold, and otherwise Loop's own figure stays:
   *   - the variant has a subscribed figure with something struck above it;
   *   - the struck element exists on Loop's Subscribe card (Loop renders it only when it strikes);
   *   - its text is Loop's DEFAULT for this variant -- its digits equal the variant price in cents.
   *     That proves Loop is showing THIS variant's card and has not been restyled, and it holds in
   *     any number format Loop picks ("$69.99", "69,99 $");
   *   - it does not already say what we want. Writing only on a difference is what stops this
   *     re-firing: the write is a mutation our observer and assets/loop-plan-guard.js both see, and
   *     the next pass finds nothing to change. */
  function patchLoopStrike() {
    const src = vsrcFor(state.variantId);
    const want = src ? figText(src.querySelector('[data-sp-was][data-mode="subscribe"]')) : '';
    const cents = src ? Number(src.dataset.spPriceCents) : NaN;
    if (!want || !(cents > 0)) return;
    const box = loopBox();
    if (!box) return;
    box.querySelectorAll('[data-loop-widget-selling-plan-group] .loop-widget-purchase-option-compare-at-price')
      .forEach((el) => {
        const now = el.textContent.trim();
        if (now === want) return;
        if (Number(now.replace(/\D/g, '')) !== cents) return;
        el.textContent = want;
      });
  }

  /* R3-6: the callout's line, and where the callout sits. 6-Pack: under OUR purchase cards, above the
   * Add button -- its home in the Liquid. Single / 3-Pack: directly after LOOP'S block, whose cards
   * are the purchase cards then; if Loop's block cannot be located, it stays home, at the foot of our
   * control. Shown only when there is a subscription to pick: always for the 6-Pack, and for Single
   * / 3-Pack only while Loop's widget is on screen offering one. A move happens only when the
   * element is not already in place, and never inside Loop's block, so it cannot wake the observer
   * below. */
  function placeCallout(panel) {
    if (!callout) return;
    let key = null;
    let after = null;
    if (state.six) {
      if (panel) key = 'six:' + panel.dataset.threeId;
    } else {
      const L = readLoop();
      const wrap = L.wrap;
      const shown = L.hasSub && (!wrap || (!wrap.hidden && !wrap.classList.contains(SUPPRESSED)));
      if (shown) {
        key = 'v:' + state.variantId;
        if (wrap && wrap.parentNode) after = wrap;
      }
    }
    let any = false;
    calloutTexts.forEach((t) => {
      const on = t.dataset.spCalloutFor === key;
      t.hidden = !on;
      if (on) any = true;
    });
    callout.hidden = !any;
    if (after) {
      if (after.nextElementSibling !== callout) after.after(callout);
    } else if (callout.nextElementSibling !== actions) {
      actions.before(callout);
    }
  }

  /* LOOP RE-RENDERS ARE NOT OBSERVABLE THROUGH ANY EVENT: it rebuilds its widget from its own
   * MutationObserver on the product form, after the theme's variant change, and at page load after
   * its CDN fetches; and a One-time <-> Subscribe click changes only classes. So Loop's block is
   * observed -- childList for re-renders, and `class` / `aria-checked` for selection -- and each burst
   * is coalesced into ONE render() per frame.
   *
   * WHY THIS CANNOT LOOP. Inside Loop's block, render() writes only (1) the suppression class on the
   * block wrapper, only when the 6-Pack selection changes, (2) at most one click per Loop element per
   * intent (above), and (3) patchLoopStrike()'s text, only when it differs. Each is a fixed point: the
   * render it triggers finds nothing to change and writes nothing. assets/loop-plan-guard.js observes
   * the whole document for childList and its own writes are idempotent the same way, so the two
   * settle together. Wrappers are observed once each (WeakSet); a wrapper the theme replaced is
   * picked up on the next render. */
  let loopSyncQueued = false;
  function scheduleLoopSync() {
    if (loopSyncQueued) return;
    loopSyncQueued = true;
    requestAnimationFrame(() => { loopSyncQueued = false; render(); });
  }

  const loopObserver = typeof MutationObserver === 'function' ? new MutationObserver(scheduleLoopSync) : null;
  const watchedLoop = new WeakSet();
  function watchLoop() {
    if (!loopObserver) return;
    loopBlocks().forEach((w) => {
      if (watchedLoop.has(w)) return;
      watchedLoop.add(w);
      loopObserver.observe(w, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-checked'] });
    });
  }

  const inLoop = (t) => t instanceof Element && loopBlocks().some((w) => w.contains(t));

  /* The shopper's own click -- or key press: Loop's arrow keys move the selection by calling .click(),
   * which is untrusted -- inside Loop's widget wins over a pending intent. `isTrusted` is false for
   * applyLoopIntent's own click. Capture phase, so it runs before Loop's handler on the option. */
  function yieldIntentToShopper(e) {
    if (loopIntent && e.isTrusted && inLoop(e.target)) loopIntent = null;
  }

  /* A frequency change in Loop's dropdown changes the posted plan without touching a class. Loop's
   * handler stops propagation, so listen in the capture phase. */
  function onLoopChange(e) {
    if (inLoop(e.target)) scheduleLoopSync();
  }

  document.addEventListener('click', yieldIntentToShopper, true);
  document.addEventListener('keydown', yieldIntentToShopper, true);
  document.addEventListener('change', onLoopChange, true);

  /* ---- Render -------------------------------------------------------------------------------- */
  function render() {
    const panel = activePanel();

    panels.forEach((p) => { p.hidden = p !== panel; });

    if (panel) {
      const box = panel.querySelector('[data-sp-six]');
      if (box) box.setAttribute('aria-pressed', state.six ? 'true' : 'false');


      panel.querySelectorAll('[data-sp-fig]').forEach((el) => {
        el.hidden = !(el.dataset.mode === state.mode && Number(el.dataset.packs) === PRICE_PACKS);
      });
      /* Mode as well as count since 2026-09-22, mirroring the line above exactly: the reference
       * figure is now emitted per mode. state.mode is the right key here for the same reason it is
       * there -- these spans are read only by topFigure()'s state.six branch, and displayMode()
       * returns state.mode whenever state.six is true, so this flag and the [data-mode] selector
       * over there can never disagree about which mode is showing. */
      panel.querySelectorAll('[data-sp-ref-fig]').forEach((el) => {
        el.hidden = !(el.dataset.mode === state.mode && Number(el.dataset.packs) === PRICE_PACKS);
      });
    }

    /* No panel means there is no 6-Pack offer for this selection. Now that activePanel() returns
     * null instead of guessing at panels[0], clear the selection too -- otherwise a checked state
     * and a live Add button would survive with nothing behind them. The next line then restores
     * buy_buttons, the Loop widget and the sticky bar, so the page is never left unbuyable. */
    if (!panel) state.six = false;

    /* The Purchase options fieldset no longer lives INSIDE the panel -- it is emitted after the
     * step-3 heading so DOM order matches paint order (WCAG 2.4.3). Nesting used to hide it for
     * free when the panel hid; now that invariant is code, so it is asserted here over EVERY
     * fieldset rather than assumed for one. A fieldset whose data-three-id is not the active
     * panel's is the OTHER filtration type's money and must never be visible. */
    root.querySelectorAll('[data-sp-modes]').forEach((m) => {
      const mine = !!panel && m.dataset.threeId === panel.dataset.threeId;
      m.hidden = !(mine && state.six);
      if (mine) {
        m.querySelectorAll('[data-sp-mode]').forEach((r) => { r.checked = r.value === state.mode; });
        /* The per-stepper mode prices live in HERE, not in the panel, now that the fieldset is a
         * sibling. Querying them off the panel would silently match nothing and leave every
         * Purchase options price hidden. */
        m.querySelectorAll('[data-sp-modefig]').forEach((el) => {
          el.hidden = Number(el.dataset.packs) !== PRICE_PACKS;
        });
      }
    });

    actions.hidden = !state.six;
    root.classList.toggle('six-pack--active', state.six);

    /* Our buttons ARE the picker, so their pressed state tracks the native picker's checked radios,
     * never our own memory. 6-Pack is the one exception: it is a pseudo-value with no radio. */
    const packNow = nativeChecked(PACK_PARAM);
    const typeNow = nativeChecked(TYPE_PARAM);
    root.querySelectorAll('[data-sp-pack]').forEach((b) => {
      const on = b.dataset.spPack === 'six'
        ? state.six
        : !state.six && b.dataset.spPackValue === packNow;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-sp-type-value]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.spTypeValue === typeNow ? 'true' : 'false');
    });

    applyBlockSuppression(state.six);
    suppressNativePicker();

    /* ROUND 3 -- the one shared purchase choice (see "one purchase choice for every pack size"
     * above). Loop's block is (re)observed, a pending "set Loop to the shopper's choice" is applied
     * BEFORE Loop is read, and then every figure below shows the one mode displayMode() settles on. */
    watchLoop();
    applyLoopIntent();
    const shown = displayMode();

    /* The Single and 3-Pack cells' prices follow the SAME checked radio as the filter buttons just
     * above, so a cell can never show one type's money beside the other type's pressed button -- and,
     * since Round 3, the shared purchase mode, so a cell can never contradict the purchase cards. */
    cellFigs.forEach((el) => {
      el.hidden = !cellFigShown(el.dataset.spForType, el.dataset.mode, typeNow, shown, hasSubscribeFig(el));
    });
    sixFigs.forEach((el) => { el.hidden = (el.dataset.mode || 'onetime') !== shown; });

    /* AFTER the [data-sp-fig] toggling above, never before: it copies whichever figure is visible,
     * so running it earlier would copy the PREVIOUS mode's money. */
    applyPriceSwap(topFigure(panel, shown));
    /* The sticky bar follows only a SUBSCRIBED Single / 3-Pack; everything else is the theme's own. */
    applyStickyPrice(!state.six && shown === 'subscribe' ? topFigure(panel, shown) : null);
    patchLoopStrike();
    placeCallout(panel);
    syncButtonLabel();
    syncButtonWeight();
    clearBusy();
    /* Last: it acts only once the theme has swapped in a NEW stepper, which is exactly the render
     * that follows a Filtration Type change's rebuild. */
    restoreCount();
    syncAddEnabled();
    /* Cosmetic only, and last: a failure here must never stop the picker. */
    try { syncGallery(panel, packNow); } catch (err) { /* the gallery stays as the theme left it */ }
  }

  /* ---- The 6-Pack photo (Sanjeet, 2026-10-19) ------------------------------------------------
   * 6-Pack IS the 3-Pack variant, so the theme's gallery shows the 3-Pack photo. When the panel
   * carries a 6-Pack photo (data-six-media-id, found by alt text in the snippet) this moves the
   * gallery to it, and back to the 3-Pack photo when the shopper picks 3-Pack again -- the same
   * variant, so the theme fires no variant:change and would not move it itself.
   *
   * ONLY ON A CHANGE OF SELECTION. render() also runs on every Loop re-render (the observer above),
   * so moving the gallery on every pass would fight a shopper swiping through the photos. The key
   * records the last selection we acted on; an unchanged key does nothing.
   *
   * ORDER. The theme's gallery listens for variant:change on the FORM and moves to the variant's
   * own photo synchronously; our listener is on the document and renders a frame later, so on a
   * Single -> 6-Pack or a Filtration Type change this runs after the theme's move and wins.
   *
   * LEAVING FOR A SINGLE. The click checks the native radio synchronously, then the variant change
   * arrives later and the theme moves to the Single's photo. Restoring the 3-Pack photo in between
   * would flash it, so the restore happens only while the 3-Pack value is the checked one.
   *
   * Mirrors the theme's own move (assets/theme.js, ProductGallery onVariantChange): select() on a
   * scrollable carousel, scrollIntoView() on the desktop grid. Anything missing does nothing.
   *
   * WAIT FOR THE THEME. Choosing 6-Pack from a Single (or switching filter type) renders at once,
   * before the theme has loaded the 3-Pack; its variant:change then moves the gallery to the 3-Pack
   * photo. So nothing is done, and the key is left alone, until state.variantId IS this panel's
   * 3-Pack -- the render that follows that variant:change, which then wins.
   *
   * THE PHOTO STARTS HIDDEN (found on the preview theme, 2026-10-19). The theme's image-set rule
   * (product-gallery.liquid:94-110) hides every "#"-tagged photo that is not the selected variant's
   * own -- on this product it hides all of them, the tag's option name never matching -- so the
   * 6-Pack photo arrives hidden, exactly like the other 3-Pack photo. It is revealed through the
   * carousel's own filter() (which also updates the thumbnails) while 6-Pack is selected, and hidden
   * again when the shopper goes back to 3-Pack. Going to a Single, or changing filter type, needs no
   * hide here: the theme re-filters the whole gallery from fresh markup on that variant change. */
  function galleryCell(mediaId) {
    if (!mediaId) return null;
    const cell = document.querySelector(`product-gallery [data-media-id="${mediaId}"]`);
    const gallery = cell && cell.closest('product-gallery');
    return gallery && gallery.carousel ? { cell, gallery, carousel: gallery.carousel } : null;
  }
  function setPhotoHidden(g, hide) {
    if (!g || g.cell.hidden === hide || typeof g.carousel.filter !== 'function') return;
    const all = Array.from(g.carousel.allCells || []);
    const i = all.indexOf(g.cell);
    if (i < 0) return;
    const hidden = all.map((c, k) => (c === g.cell ? hide : c.hidden) ? k : -1).filter((k) => k >= 0);
    g.carousel.filter(hidden);
    /* The attribute the theme reads its own index arithmetic from: 1-based media positions. */
    g.gallery.setAttribute('filtered-indexes', JSON.stringify(hidden.map((k) => k + 1)));
  }
  function moveGallery(g) {
    if (!g || g.cell.hidden) return;
    if (g.carousel.isScrollable) {
      const index = Array.from(g.carousel.cells || []).indexOf(g.cell);
      if (index >= 0) g.carousel.select(index, { instant: true });
    } else {
      g.cell.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }
  function syncGallery(panel, packNow) {
    const settled = !!panel && state.variantId === Number(panel.dataset.threeId);
    if (state.six && !settled) return;
    const key = state.six ? 'six:' + panel.dataset.threeId : 'off';
    if (key === galleryKey) return;
    const was = galleryKey;
    galleryKey = key;
    if (state.six) {
      const six = galleryCell(panel.dataset.sixMediaId);
      setPhotoHidden(six, false);
      moveGallery(six);
    } else if (was.indexOf('six:') === 0) {
      const left = panelForVariant(Number(was.slice(4)));
      if (left && packNow === left.dataset.packValueId && left.dataset.sixMediaId) {
        setPhotoHidden(galleryCell(left.dataset.sixMediaId), true);
        moveGallery(galleryCell(left.dataset.threeMediaId));
      }
    }
  }

  /* ---- Driving the native picker -------------------------------------------------------------
   * 6-Pack IS the 3-Pack variant at quantity 2, so checking it selects the 3-Pack value in the
   * THEME's own picker. Price block, picker and this control then all describe one variant, and the
   * theme -- not us -- owns the variant change.
   *
   * The radio contract, cited at the lines that actually construct it:
   *   - the NAME is built at snippets/variant-picker.liquid:178
   *         assign name = form_id | append: '-option' | append: option.position
   *     and passed into 'option-value' as param_name (:183-189);
   *   - the VALUE is the option value's id -- snippets/option-value.liquid:56, `assign value =
   *     option_value.id`;
   *   - the radio itself is rendered at snippets/option-value.liquid:163,
   *         <input ... type="radio" name="{{ param_name }}" ... value="{{ value | escape }}" ...>
   * So name="<form id>-option<position>", value="<option value id>", which is what data-pack-param
   * and data-pack-value-id carry. *An earlier draft cited variant-picker.liquid:139, which is an
   * `{%- else -%}`; :132 and :141 are the popover variant's radios, a different render path.* */
  function setNativeOption(param, valueId) {
    if (!param || !valueId) return;
    const radio = document.querySelector(
      'input[type="radio"][name="' + CSS.escape(param) + '"][value="' + CSS.escape(String(valueId)) + '"]'
    );
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Read the CHECKED radio rather than trusting a cached id: the theme can change the variant
   * without us -- a ?variant= URL, or its own price-block controls -- and our buttons are the only
   * picker a shopper can see, so a stale pressed state would be a lie about what is selected. */
  function nativeChecked(param) {
    if (!param) return '';
    const r = document.querySelector('input[type="radio"][name="' + CSS.escape(param) + '"]:checked');
    return r ? r.value : '';
  }

  /* Round 2 (spec 5.3.3 decision 4). The Single and 3-Pack buttons are SHARED across filtration
   * types, so Liquid rendered one price pair per type into each, tagged data-sp-for-type with that
   * type's option-value id -- the same id the native radio's value carries. Show the pair for the
   * type the picker currently says. No checked radio means NOTHING is shown: a cell with no price
   * is recoverable, a cell showing the other filter's price is not.
   *
   * Round 3 (spec 5.3.4 R3-10) adds the MODE: each type has a one-time pair and, when Liquid could
   * price the variant's own plan, a subscribed pair (data-mode). Subscribe shows the subscribed pair;
   * a cell with NO subscribed pair keeps its one-time pair rather than go blank -- one-time is the
   * safe figure. A pair with no data-mode is one-time. Pure, so it is tested against this file's
   * own text. */
  function cellFigShown(forType, figMode, typeNow, mode, hasSubscribe) {
    if (!typeNow || forType !== typeNow) return false;
    const want = mode === 'subscribe' && hasSubscribe ? 'subscribe' : 'onetime';
    return (figMode || 'onetime') === want;
  }

  function selectThreePackInNativePicker() {
    const panel = activePanel();
    if (!panel) return;
    setNativeOption(PACK_PARAM, panel.dataset.packValueId);
  }

  /* Round 3. The variant a Single / 3-Pack selection WILL land on -- the one whose Liquid figure
   * source carries that pack and filter type -- so a Loop intent knows which rendering to wait for.
   * 0 when there is none, which queues nothing. */
  function targetVariantId(pack, typeValueId) {
    const s = vsrcs.find((x) => x.dataset.spSrcPack === pack && x.dataset.spTypeValueId === typeValueId);
    return s ? Number(s.dataset.spVariantId) : 0;
  }

  /* ---- The theme's variant change ------------------------------------------------------------ */
  document.addEventListener('variant:change', (event) => {
    const v = event && event.detail && event.detail.variant;
    if (!v || !v.id) return;
    state.variantId = Number(v.id);
    /* The shopper moved the native picker to a Single while the 6-Pack was checked. Uncheck rather
     * than fight them back to 3-Pack. When WE flipped the picker, this variant IS a 3-Pack and the
     * selection survives -- which is exactly AC-13 across a Filtration Type change. */
    if (state.six && !panelForVariant(state.variantId)) state.six = false;
    msg('');
    /* theme.js:3684-3703 has just run over the rerenderable blocks. None of our three suppression
     * targets loses its wrapper to it (see THE WRAPPERS SURVIVE THE PARTIAL RERENDER above), so
     * this re-apply is belt-and-braces, not the mechanism. It also clears the Add button's disabled
     * state once the theme has settled on the new variant. */
    requestAnimationFrame(render);
  });

  /* ---- The two tooltips (Round 2, plan Task 11; restyled in Round 3, spec 5.3.4 R3-5) --------
   * Each bubble is a real element -- Loop's green tooltip with a title line, opening BELOW its link --
   * that snippets/six-pack-picker.liquid renders INSIDE its trigger button and shows on hover,
   * keyboard focus, or TIP_OPEN. This is the minimum that makes a TAP reveal it: iOS Safari does not
   * focus a button on tap, so :focus alone never fires there. Nothing is created and nothing is
   * positioned here -- two classes, nothing else. (Until Round 3 the bubble was the theme's
   * `data-tooltip` ::after, which could not carry a title. It became an element, and stayed inside
   * the button so that every press on it still arrives here, exactly as a press on the ::after did.)
   *
   *   TIP_OPEN   -- toggled by a click or tap; one tip open at a time; closed by a press anywhere
   *                 else (a mouse pointerdown, the end of a touch or pen tap, or a click -- see
   *                 below), by Escape, or by focus leaving it.
   *   TIP_CLOSED -- "the shopper closed it while it is still hovered or focused" (a second tap or
   *                 click, or Escape), which would otherwise keep showing it through :hover or
   *                 :focus-visible. Cleared when focus leaves, and when the pointer leaves UNLESS the
   *                 trigger is still :focus-visible (see onTipLeave), so the next hover or focus
   *                 shows it again. On touch, pointerleave fires BEFORE click, so a tap's own
   *                 pointerleave cannot undo the close that tap is about to make. */
  const TIP_OPEN = 'is-tip-open';
  const TIP_CLOSED = 'is-tip-closed';

  function toggleTip(tip) {
    const open = !tip.classList.contains(TIP_OPEN);
    tips.forEach((t) => { if (t !== tip) t.classList.remove(TIP_OPEN); });
    tip.classList.toggle(TIP_OPEN, open);
    tip.classList.toggle(TIP_CLOSED, !open);
  }

  /* A press on a trigger is either on the LINK or on its SHOWING TOOLTIP -- the bubble is inside the
   * button and catches the pointer only while shown (see the tooltip stylesheet), so both arrive here
   * with the button as the nearest [data-sp-tip]. Told apart by position: getBoundingClientRect() is
   * the button's OWN box, which its absolutely positioned bubble does not enlarge, so a press outside
   * it landed on the tooltip, and that CLOSES it -- it must never pin a hover-shown tooltip open, and
   * it must never reach what the tooltip covers. A keyboard activation (e.detail === 0) has no
   * meaningful position and toggles. Review 2026-09-16: the tooltip used to ignore presses, so
   * tapping its text to close it also pressed the quantity stepper or pack card underneath. Since
   * Round 3 the bubbles open DOWNWARD -- step 1's over the filter cards, step 3's over the purchase
   * cards or Loop's options -- and the rule is the same.
   *
   * Two details, both from the second review on 2026-09-16:
   *   - EDGE: a click's clientX/Y are whole pixels while the box is fractional, so a press on the
   *     link's own edge could read as outside it. A 1px tolerance keeps the link's edge the link's.
   *   - A bubble press closes EVERY tooltip, not only this one. Safari does not focus a button on
   *     click, so no focusout would close a tooltip pinned open elsewhere, and two could show. */
  const TIP_EDGE = 1;
  function pressTip(tip, e) {
    const r = tip.getBoundingClientRect();
    const onBubble = e.detail !== 0 &&
      (e.clientX < r.left - TIP_EDGE || e.clientX > r.right + TIP_EDGE ||
       e.clientY < r.top - TIP_EDGE || e.clientY > r.bottom + TIP_EDGE);
    if (!onBubble) { toggleTip(tip); return; }
    tips.forEach((t) => t.classList.remove(TIP_OPEN));
    tip.classList.add(TIP_CLOSED);
  }

  /* The pointer left a trigger. Clearing TIP_CLOSED lets the NEXT hover show the tooltip again -- but
   * NOT while the trigger is still :focus-visible. Review 2026-09-16 (medium): Chrome treats a button
   * as keyboard-focused after Escape, so clearing it there brought a dismissed tooltip straight back
   * through the :focus-visible rule the moment the mouse moved away, and because a SHOWING tooltip
   * catches presses, every click on the card it covered was eaten (3 of 3). Focus leaving still
   * clears it. A mouse-focused button is not :focus-visible, so ordinary hover-then-leave is
   * unchanged. `matches(':focus-visible')` throws in an engine without the selector; there the
   * safer reading is "still focused". */
  function onTipLeave(t) {
    let keyboardShown;
    try { keyboardShown = t.matches(':focus-visible'); }
    catch (_) { keyboardShown = t === document.activeElement; }
    if (!keyboardShown) t.classList.remove(TIP_CLOSED);
  }

  tips.forEach((t) => {
    t.addEventListener('focusout', () => { t.classList.remove(TIP_OPEN, TIP_CLOSED); });
    t.addEventListener('pointerleave', () => onTipLeave(t));
  });

  /* Dismissal by pressing anywhere else. POINTERDOWN, not only click: iOS Safari sends no click for a
   * tap on content it does not treat as clickable -- plain text, the page background -- and a
   * delegated listener on document does not make anything clickable. With click alone, a tapped
   * tooltip stayed open over the price block until the link itself was tapped again. pointerdown
   * fires for every touch. click is kept for a keyboard activation elsewhere, which has no pointer
   * event. Both skip the triggers, so a trigger's own tap still reaches toggleTip() through root's
   * click listener -- including a tap on a SECOND trigger, which toggleTip() handles. Pure apart from
   * the `tips` it closes, so it is tested against this file's own text. */
  function closeTipsOutside(e) {
    if (e.target instanceof Element && e.target.closest('[data-sp-tip]')) return;
    tips.forEach((t) => t.classList.remove(TIP_OPEN));
  }

  /* TOUCH AND PEN CLOSE AT THE END OF A TAP, NOT AT THE START OF A PRESS (review 2026-09-21). Since
   * Round 3 the bubble opens DOWNWARD, so on a phone step 3's often runs below the fold and the shopper
   * has to scroll to read it. Every swipe begins with a pointerdown, so closing on pointerdown shut the
   * tooltip the moment the swipe to read it began. A browser ends a pan with pointercancel and a tap
   * with pointerup, so a touch or pen press closes the tips on its pointerup, and only if no
   * pointercancel came first. Pointer events, unlike click, fire for EVERY touch -- iOS Safari included,
   * on plain text too -- so the iOS dismissal above still holds. The press's target is kept from its
   * pointerdown, where the press began. A mouse cannot pan the page, so it still closes on pointerdown. */
  let tipPress = null; // { pointerId, target } of the touch or pen press in progress
  function onTipPointerDown(e) {
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') { closeTipsOutside(e); return; }
    tipPress = { pointerId: e.pointerId, target: e.target };
  }
  function onTipPointerEnd(e) {
    if (!tipPress || e.pointerId !== tipPress.pointerId) return;
    const press = tipPress;
    tipPress = null;
    if (e.type === 'pointerup') closeTipsOutside(press);
  }

  if (tips.length) {
    document.addEventListener('pointerdown', onTipPointerDown);
    document.addEventListener('pointerup', onTipPointerEnd);
    document.addEventListener('pointercancel', onTipPointerEnd);
    document.addEventListener('click', closeTipsOutside);
    /* Escape dismisses without moving focus -- the "dismissible" clause of WCAG 1.4.13. "Hoverable"
     * is met by the stylesheet (a shown tooltip catches the pointer; see pressTip). Only a tip that
     * is actually showing is marked closed: marking one that is not would stop its NEXT hover from
     * showing it. */
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      tips.forEach((t) => {
        if (t.classList.contains(TIP_OPEN) || t === document.activeElement || t.matches(':hover')) {
          t.classList.remove(TIP_OPEN);
          t.classList.add(TIP_CLOSED);
        }
      });
    });
  }

  /* ---- Input --------------------------------------------------------------------------------- */
  root.addEventListener('change', (e) => {
    /* [data-sp-six] is a BUTTON now, not a checkbox -- it is handled in the click listener below
     * with the other two Pack Size buttons, so all three share one code path. */
    if (e.target.matches('[data-sp-mode]')) {
      state.mode = e.target.value;
      msg('');
      render();
    }
  });

  root.addEventListener('click', (e) => {
    /* A tooltip trigger selects nothing. Handled first and returned from, so it can never fall
     * through to the pack or filter handlers below. */
    const tip = e.target.closest('[data-sp-tip]');
    if (tip) { pressTip(tip, e); return; }

    /* aria-disabled is ADVISORY -- unlike `disabled` it does not stop the click reaching us -- so
     * the handler has to honour it, or a sold-out value would still be selectable. */
    if (e.target.closest('[aria-disabled="true"]')) { e.preventDefault(); return; }

    /* PACK SIZE. Single and 3-Pack set the native picker directly. 6-Pack selects the 3-Pack value
     * that already lives on its own panel as data-pack-value-id -- one id, one source -- and enters
     * 6-Pack mode. Leaving 6-Pack is implicit in choosing either real value. */
    const packBtn = e.target.closest('[data-sp-pack]');
    if (packBtn) {
      msg('');
      const wasSix = state.six;
      /* ROUND 3 (R3-10): the one-time / subscribe choice AS IT STANDS BEFORE THIS CLICK -- ours on
       * the 6-Pack, Loop's otherwise, and while Loop is still loading the arrival default that is on
       * its way to it (R3-12) -- carried to the new pack. */
      const choice = currentChoice();
      if (packBtn.dataset.spPack === 'six') {
        state.six = true;
        /* Arriving from Single / 3-Pack: our radio takes that choice. NOTHING readable leaves
         * state.mode alone (R3-12) -- which is the page's arrival default until the shopper has
         * touched a purchase card, and their own last 6-Pack choice after that. It used to be forced
         * to 'onetime' here, which threw that information away: with Loop absent or still loading,
         * a shopper who had never chosen anything watched our Subscribe card, checked by the Liquid,
         * flip to One-time the moment they picked 6-Pack. The fail-safe-to-one-time rule is about
         * pricing an option the CART may not receive; it does not apply to our own fieldset, which
         * is the only thing the 6-Pack add reads (see the add handler's planId). */
        if (!wasSix && choice) state.mode = choice;
        loopIntent = null;
        selectThreePackInNativePicker();
      } else {
        state.six = false;
        setNativeOption(PACK_PARAM, packBtn.dataset.spPackValue);
        /* Arriving at Single / 3-Pack: Loop is set to the choice once it shows the new variant. */
        queueLoopIntent(choice, targetVariantId(packBtn.dataset.spPack, nativeChecked(TYPE_PARAM)));
      }
      /* Entering or leaving 6-Pack starts the count at 1. The theme already does this for
       * Single <-> 3-Pack, by rebuilding the stepper on the variant change. But 3-Pack <-> 6-Pack is
       * the SAME variant, so nothing is rebuilt -- and a "2" would silently change meaning from six
       * filters to twelve, or back. That is a money surprise, so reset it explicitly. */
      if (wasSix !== state.six) {
        pendingCount = null;
        if (sixPackCount() !== 1) setCount(themeStepper(), 1);
      }
      render();
      return;
    }

    /* FILTER TYPE. This row is the ONLY Filtration Type selector left on the page, so it drives the
     * native picker for every value of the option -- including any with no 6-Pack panel, which
     * would otherwise be unreachable at any pack size.
     *
     * Snapshot the count BEFORE driving the picker (AC-13): the variant change that follows
     * rebuilds the stepper at 1, and restoreCount() puts this value back on the new one. */
    const typeBtn = e.target.closest('[data-sp-type-value]');
    if (typeBtn) {
      msg('');
      if (state.six) pendingCount = { value: sixPackCount(), from: themeStepper() };
      /* ROUND 3: on Single / 3-Pack the purchase choice is Loop's, and the variant is about to
       * change under it. Loop carries its own choice across its re-render today; the intent makes
       * that a checked fact rather than an assumption about Loop. (The 6-Pack's is state.mode,
       * which a type change does not touch.) */
      const choice = state.six ? null : currentChoice();
      const packNow = nativeChecked(PACK_PARAM);
      const cur = vsrcs.find((s) => s.dataset.spPackValueId === packNow);
      setNativeOption(TYPE_PARAM, typeBtn.dataset.spTypeValue);
      if (!state.six) queueLoopIntent(choice, cur ? targetVariantId(cur.dataset.spSrcPack, typeBtn.dataset.spTypeValue) : 0);
      render();
    }
  });

  /* ---- Cart ---------------------------------------------------------------------------------- */

  /* Shopify MERGES lines with the same variant AND the same selling plan. Adding on top of one would
   * silently take an existing line to an odd quantity. Refuse narrowly: same variant AND same plan
   * state only. A different filter type, or an existing 6-MONTH subscriber, must still work.
   *
   * This control only ever adds a 6-Pack, so the refusal cannot reach an ordinary Single or 3-Pack
   * add -- those go through the theme's own buy buttons and are untouched (AC-6). The earlier draft
   * ran this check on every add regardless of what was selected, which would have refused a plain
   * repeat add of a Single or a 3-Pack.
   *
   * IT FAILS CLOSED, AND IT USED TO FAIL OPEN. *An earlier draft returned `false` on a failed cart
   * read (`if (!res.ok) return false;`), and let an exception from fetch or .json() fall through to
   * the caller's generic catch.* A cart read that does not come back is not evidence that the cart
   * is clear. On one transient cart.js error a duplicate 6-Pack goes through, Shopify MERGES it into
   * the existing line (same variant, same plan -- the exact condition this function is testing for),
   * the line lands at quantity 4, the drawer badge reads `6-Pack x 2`, and the shopper is charged
   * for twelve filters they did not ask for. That is a money defect reached by a network blip, and
   * every other failure path in this design refuses rather than proceeding.
   *
   * Three states, not two, because "I could not tell" is a real answer and must not be spelled the
   * same as "no". Only 'no' is allowed to proceed. A 200 with no `items` ARRAY is 'unknown' too --
   * that is what an app-injected or proxied response looks like, and `(cart.items || [])` would have
   * silently read it as an empty cart. */
  async function collides(variantId, planId) {
    let cart;
    try {
      const res = await fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return 'unknown';
      cart = await res.json();
    } catch (err) {
      return 'unknown';
    }
    if (!cart || !Array.isArray(cart.items)) return 'unknown';
    return cart.items.some((it) => {
      if (Number(it.id) !== variantId) return false;
      const linePlan = it.selling_plan_allocation
        ? Number(it.selling_plan_allocation.selling_plan.id)
        : null;
      return linePlan === planId;
    }) ? 'yes' : 'no';
  }

  /* UpCart does not listen for a `cart:refresh` CustomEvent. Open the drawer through UpCart's own
   * ladder, exactly as assets/sf-sub-bundle-picker.js:604-619 does. `cart:refresh` is still
   * dispatched, but only so the THEME's own header count updates. */
  function openCart() {
    try {
      if (typeof window.upcartRegisterAddToCart === 'function') { window.upcartRegisterAddToCart(); return; }
      if (typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); return; }
      const link = document.querySelector('.header__cart-link a, a[href$="/cart"]');
      if (link) { link.click(); return; }
      window.location = routeRoot() + 'cart';
    } catch (e) {
      window.location = routeRoot() + 'cart';
    }
  }

  addBtn.addEventListener('click', async () => {
    if (!state.six) return;                       // the control adds 6-Packs and nothing else
    /* One request at a time. syncAddEnabled() keeps the button disabled while `adding`; this is the
     * second gate, for anything that re-enables it behind our back. */
    if (adding) return;
    const panel = panelForVariant(state.variantId);
    /* syncAddEnabled() disables the button in exactly this state, so this is a second gate. It
     * re-drives the picker rather than failing silently if it is ever reached. */
    if (!settled(panel)) { syncAddEnabled(); if (!panel) selectThreePackInNativePicker(); return; }

    /* EVERYTHING that decides the line is captured HERE, synchronously, at the click -- and then
     * re-checked after the cart read below. *Found in review 2026-09-11: the count used to be read
     * after `await collides()`, while a comment claimed it was read "at the moment they click". The
     * stepper and our Pack/Filter buttons stay live during that round trip, so the shopper could
     * change what they had chosen and still have the old click post a mixture of the two.* */
    const variantId = Number(panel.dataset.threeId);
    /* A plan applies ONLY to a subscribed 6-Pack. Single and 3-Pack keep the theme's own Loop
     * widget, so a subscribe choice made there is not ours to send. */
    const planId = state.mode === 'subscribe' ? Number(panel.dataset.planId) : null;
    /* Off the stepper the shopper is looking at, never from anything remembered: that is what
     * guarantees what is added is what was displayed. Two 3-Packs per 6-Pack. */
    const count = sixPackCount();

    msg('');
    addBtn.disabled = true;
    adding = true;
    /* The theme's own button-content watches its button for aria-busy and shows its loading state
     * (assets/theme.js, ButtonContent), so ours BEHAVES like the theme's Add to Cart while the
     * request is in flight, not merely looks like it. Cleared through clearBusy() -- see there. */
    addBtn.setAttribute('aria-busy', 'true');
    try {
      /* Fail closed on 'unknown': we do not add what we could not check. The two refusals get
       * DIFFERENT copy on purpose -- "you already have this" is a fact about the cart, and telling a
       * shopper that when we never read the cart sends them to look for a line that is not there. */
      const verdict = await collides(variantId, planId);
      if (verdict === 'unknown') { msg(COPY.cartcheck); return; }
      if (verdict === 'yes') { msg(COPY.collide); return; }

      /* The selection moved while we were reading the cart. Posting now would add what was chosen
       * a moment ago, not what the page shows -- so add nothing, silently. The shopper's next click
       * is taken against the new selection. */
      if (!state.six
        || panelForVariant(state.variantId) !== panel
        || (state.mode === 'subscribe') !== (planId !== null)
        || sixPackCount() !== count) {
        return;
      }

      const item = { id: variantId, quantity: count * 2 };
      if (planId) item.selling_plan = planId;

      const res = await fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      });

      if (!res.ok) {
        /* A 422 is NOT necessarily stock. An unallocated or wrong selling plan returns 422 too, and
         * telling that shopper "Only 1 left" is a lie that sends them to a dead end. Read the body
         * and use the stock copy only when the body names inventory. The sibling's handler echoes a
         * bare status code (origin/Toby-Rock:snippets/toby-rock-picker.liquid:1822), which AC-16
         * explicitly rejects -- so this branch is new work, not a port. */
        let body = null;
        try { body = await res.json(); } catch (err) { body = null; }
        const detail = String((body && (body.description || body.message)) || '');
        /* INVENTORY-SPECIFIC TOKENS ONLY, each on a word boundary. *An earlier draft included a bare
         * `available` here*, which re-creates the exact mislabel this branch exists to prevent:
         * Shopify's selling-plan 422s say "...is not available" and would have been reported to the
         * shopper as "Only 1 left" -- a lie that sends them to a dead end while the real fault is a
         * misconfigured plan. The kept tokens all come from Shopify's own inventory copy ("You can
         * only add N...", "All N ... are in your cart"). */
        const isStock = /\b(only add|in your cart|stock|inventory|sold out)\b/i.test(detail);
        /* "Only 1 left, and a 6-Pack needs 2" is true only for ONE 6-Pack. Since the count became
         * the theme's uncapped stepper (2026-09-11), a shopper asking for several can be refused
         * with plenty in stock, and that sentence would be a lie. So a count above one gets the
         * count-free wording -- falling back to the original only if Liquid did not supply it. */
        const stockMsg = count > 1 ? (COPY.stockMany || COPY.stock) : COPY.stock;
        msg(isStock ? stockMsg : COPY.error);
        if (!isStock) console.warn('[six-pack] add failed', res.status, detail);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      openCart();
    } catch (err) {
      msg(COPY.error);
    } finally {
      adding = false;
      clearBusy();
      /* Not `addBtn.disabled = false`: if the shopper moved the picker while the request was in
       * flight, re-enabling unconditionally hands back a button with no panel behind it. */
      syncAddEnabled();
    }
  });

  /* ENTER IN THE STEPPER. Found in review 2026-09-11, and introduced by making the theme's stepper
   * the 6-Pack count. That stepper's input is name="quantity" form="<product form>"
   * (product-info.liquid:173, quantity-selector.liquid:54): it still belongs to the THEME's product
   * form. Type a number and press Enter -- or Go on Android -- and the browser implicitly submits
   * that form; the theme's ProductForm (assets/theme.js ~3228) then posts id = 3-Pack, quantity = N.
   * That is N 3-Packs instead of 2N, it bypasses collides(), and it drops the shopper's
   * subscription choice.
   *
   * So while 6-Pack is selected, that form's submission is taken over and routed to OUR Add. A
   * capture-phase listener on document runs before ProductForm's own listener on the form, and
   * stopPropagation keeps that one from ever running. No legitimate submission is lost: in 6-Pack
   * mode the theme's buy_buttons and sticky bar -- the only other things that submit this form --
   * are already suppressed. The form is found through the stepper's own input, so nothing is
   * hardcoded, and with no stepper there is nothing to intercept. */
  document.addEventListener('submit', (e) => {
    if (!state.six) return;
    const el = themeStepper();
    const input = el && el.querySelector('input');
    if (!input || !input.form || e.target !== input.form) return;
    e.preventDefault();
    e.stopPropagation();
    if (!addBtn.disabled) addBtn.click();
  }, true);

  /* ---- Arrival (spec 5.3.4 R3-12, Sanjeet 2026-09-22) -----------------------------------------
   * The page's purchase default is DEFAULT_MODE, read off the Liquid's `checked` at the top of this
   * file, and it is one default for all three pack sizes. The 6-Pack already has it -- it is
   * state.mode, and the Liquid checked that radio. The Single and the 3-Pack do not: their purchase
   * cards are LOOP'S, and Loop's own default comes from a store-wide admin preference we cannot set
   * from the theme (loop_widget.js selectPurchaseOption: `purchaseOptionLabel === "Subscription"`).
   * So the page ASKS for it, through the same intent Lane C built for a pack change -- Loop's own
   * click path, never a write into Loop's markup or its state.
   *
   * Queued BEFORE the first render() so the very first pass already carries it: render() calls
   * applyLoopIntent() and THEN reads Loop for every figure it shows, in that order, so Loop's
   * selection and our price, badge and pack cells change together, in one frame, and the page is
   * never caught pricing one option while Loop shows the other.
   *
   * It is a default, not a preference: the first deliberate thing the shopper does cancels it (a
   * real click or key inside Loop -- yieldIntentToShopper) or replaces it (a pack or filter change,
   * which re-queues from currentChoice()). Nothing re-queues it, so once it is spent or cancelled
   * the shopper's choice is the only choice on the page. */
  queueLoopIntent(DEFAULT_MODE, state.variantId);

  render();
})();
